import { FormEvent, useState } from 'react';
import { createPractitionerContactRequest } from '@/lib/api';

const inputClass =
  'w-full px-4 py-2.5 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition';
const labelClass = 'block text-xs font-semibold text-foreground-700 mb-1.5';

type ContactFormProps = {
  // Besoins sélectionnés à l'étape précédente (parcours « Demander un devis »).
  preselectedOptions?: string;
  submitLabel?: string;
  requestType?: 'quote' | 'visibility-study' | 'contact';
};

// Formulaire de contact praticien : « vous êtes », nom, prénom, téléphone,
// e-mail, établissement, ville, adresse, fonction et message.
export default function ContactForm({ preselectedOptions, submitLabel = 'Envoyer ma demande', requestType = 'contact' }: ContactFormProps) {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    setStatus('sending');
    setFormError('');

    try {
      const formData = new FormData(form);
      const payload = Object.fromEntries(
        Array.from(formData.entries()).map(([key, value]) => [key, String(value)]),
      );
      payload.request_type = requestType;
      await createPractitionerContactRequest(payload);
      setStatus('success');
      form.reset();
    } catch (error) {
      setStatus('error');
      setFormError(error instanceof Error ? error.message : "Une erreur est survenue lors de l'envoi. Merci de réessayer.");
    }
  };

  if (status === 'success') {
    return (
      <div className="flex flex-col items-center justify-center text-center py-10">
        <span className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
          <i className="ri-check-line text-2xl w-6 h-6 flex items-center justify-center" />
        </span>
        <h3 className="font-heading text-xl font-semibold text-foreground-950 mb-2">Demande envoyée !</h3>
        <p className="text-sm text-foreground-700 max-w-md leading-relaxed">
          Merci, nous avons bien reçu votre demande. Un conseiller MEDICACLIC vous recontacte sous
          24 h ouvrées avec une proposition adaptée.
        </p>
        <button
          type="button"
          onClick={() => setStatus('idle')}
          className="mt-6 px-5 py-2.5 rounded-md bg-background-100 hover:bg-background-200 border border-background-300 text-foreground-800 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
        >
          Envoyer une autre demande
        </button>
      </div>
    );
  }

  return (
    <form data-readdy-form id="practitioner-contact-form" onSubmit={handleSubmit} noValidate>
      <div>
        <label htmlFor="contact-vous-etes" className={labelClass}>Vous êtes</label>
        <select id="contact-vous-etes" name="vous_etes" defaultValue="Profession libérale" className={`${inputClass} cursor-pointer`}>
          <option value="Profession libérale">Profession libérale</option>
          <option value="Société">Société</option>
        </select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
        <div>
          <label htmlFor="contact-nom" className={labelClass}>Nom</label>
          <input id="contact-nom" name="nom" type="text" required placeholder="Ex. Alaoui" className={inputClass} />
        </div>
        <div>
          <label htmlFor="contact-prenom" className={labelClass}>Prénom</label>
          <input id="contact-prenom" name="prenom" type="text" required placeholder="Ex. Karim" className={inputClass} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
        <div>
          <label htmlFor="contact-phone" className={labelClass}>Téléphone</label>
          <input id="contact-phone" name="phone" type="tel" required placeholder="+212 6 00 00 00 00" className={inputClass} />
        </div>
        <div>
          <label htmlFor="contact-email" className={labelClass}>E-mail</label>
          <input id="contact-email" name="email" type="email" required placeholder="vous@exemple.com" className={inputClass} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
        <div>
          <label htmlFor="contact-etablissement" className={labelClass}>Établissement</label>
          <input id="contact-etablissement" name="etablissement" type="text" placeholder="Ex. Cabinet Atlas" className={inputClass} />
        </div>
        <div>
          <label htmlFor="contact-ville" className={labelClass}>Ville</label>
          <input id="contact-ville" name="ville" type="text" placeholder="Ex. Rabat" className={inputClass} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
        <div>
          <label htmlFor="contact-adresse" className={labelClass}>Adresse</label>
          <input id="contact-adresse" name="adresse" type="text" placeholder="Ex. 12 Av. Hassan II" className={inputClass} />
        </div>
        <div>
          <label htmlFor="contact-fonction" className={labelClass}>Fonction</label>
          <input id="contact-fonction" name="fonction" type="text" placeholder="Ex. Gérant, Médecin" className={inputClass} />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="contact-message" className={labelClass}>Message</label>
        <textarea
          id="contact-message"
          name="message"
          rows={4}
          maxLength={500}
          placeholder="Précisez votre besoin en quelques lignes..."
          className={`${inputClass} resize-none`}
        />
        <p className="mt-1 text-xs text-foreground-400">500 caractères maximum.</p>
      </div>

      {preselectedOptions && <input type="hidden" name="besoins" value={preselectedOptions} readOnly />}

      <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="mc-hp-field" />

      {status === 'error' && formError && (
        <div className="mt-4 flex items-start gap-2 rounded-md bg-accent-100 border border-accent-300 px-4 py-3">
          <i className="ri-error-warning-line text-accent-700 text-base w-4 h-4 flex items-center justify-center mt-0.5" />
          <p className="text-xs text-accent-900 leading-relaxed">{formError}</p>
        </div>
      )}

      <button
        type="submit"
        disabled={status === 'sending'}
        className="mt-5 w-full flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-background-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
      >
        {status === 'sending' ? (
          <>
            <i className="ri-loader-4-line animate-spin text-base w-4 h-4 flex items-center justify-center" />
            Envoi en cours...
          </>
        ) : (
          <>
            <i className="ri-send-plane-line text-base w-4 h-4 flex items-center justify-center" />
            {submitLabel}
          </>
        )}
      </button>
    </form>
  );
}